import { useEffect, useRef, useState, useMemo } from "react";
import { X, Play, ChevronLeft, ChevronRight } from "lucide-react";
import { cdnTransform } from "../lib/cloudinary";

/**
 * Premium media gallery for product pages (gemstones + auctions).
 *
 * Props:
 *  - images: [{ url, alt, public_id }]  (max 5 in practice, but no hard cap here)
 *  - video : { type: "upload"|"youtube", url, thumbnail_url, embed_url? }
 *  - fallbackImage: string  (legacy `image_url` used when `images` is empty)
 *  - fallbackGallery: string[] (legacy `gallery` used when `images` is empty)
 *  - alt: string  (page-level alt fallback)
 */
const MediaGallery = ({ images = [], video = null, fallbackImage, fallbackGallery = [], alt = "" }) => {
  // Compose the effective image list: prefer structured `images`, fall back to legacy fields.
  const effectiveImages = useMemo(() => {
    if (Array.isArray(images) && images.length > 0) {
      return images
        .slice()
        .sort((a, b) => (a.order ?? 0) - (b.order ?? 0))
        .map((im, i) => ({ url: im.url, alt: im.alt || alt, key: im.public_id || `${im.url}-${i}` }));
    }
    const legacy = [fallbackImage, ...(fallbackGallery || [])].filter(Boolean);
    return [...new Set(legacy)].map((u, i) => ({ url: u, alt, key: `legacy-${i}` }));
  }, [images, fallbackImage, fallbackGallery, alt]);

  const [activeIdx, setActiveIdx] = useState(0);
  const [videoOpen, setVideoOpen] = useState(false);
  const [zoom, setZoom] = useState({ on: false, x: 50, y: 50 });
  const mainRef = useRef(null);

  // Reset when images change
  useEffect(() => { setActiveIdx(0); }, [effectiveImages.length]);

  const cover = effectiveImages[activeIdx] || effectiveImages[0];
  const hero = cover?.url ? cdnTransform(cover.url, "w_1600,q_auto,f_auto") : null;
  const heroAlt = cover?.alt || alt;

  const next = () => setActiveIdx((i) => (i + 1) % Math.max(effectiveImages.length, 1));
  const prev = () => setActiveIdx((i) => (i - 1 + effectiveImages.length) % Math.max(effectiveImages.length, 1));

  // Touch swipe for mobile carousel
  const touchX = useRef(0);
  const onTouchStart = (e) => { touchX.current = e.touches[0].clientX; };
  const onTouchEnd = (e) => {
    const dx = (e.changedTouches[0].clientX) - touchX.current;
    if (dx > 50) prev();
    else if (dx < -50) next();
  };

  // Desktop zoom — track mouse over main image
  const onMove = (e) => {
    if (!mainRef.current) return;
    const r = mainRef.current.getBoundingClientRect();
    const x = ((e.clientX - r.left) / r.width) * 100;
    const y = ((e.clientY - r.top) / r.height) * 100;
    setZoom({ on: true, x, y });
  };
  const onLeave = () => setZoom({ on: false, x: 50, y: 50 });

  if (!hero) return <div className="aspect-[4/5] bg-noir border border-white/10" />;

  return (
    <div data-testid="media-gallery" className="select-none">
      {/* MAIN IMAGE */}
      <div
        ref={mainRef}
        className="relative aspect-[4/5] overflow-hidden bg-black cursor-zoom-in"
        onMouseMove={onMove}
        onMouseLeave={onLeave}
        onTouchStart={onTouchStart}
        onTouchEnd={onTouchEnd}
        data-testid="gallery-main"
      >
        <img
          src={hero}
          alt={heroAlt}
          loading="eager"
          decoding="async"
          className="w-full h-full object-cover transition-transform duration-300 ease-out"
          style={zoom.on ? { transform: "scale(1.8)", transformOrigin: `${zoom.x}% ${zoom.y}%` } : undefined}
        />
        {/* VIDEO PLAY — bottom-left chip so it does not obscure the gemstone. */}
        {video && video.url && (
          <button
            type="button"
            data-testid="gallery-play-video"
            onClick={() => setVideoOpen(true)}
            className="absolute left-3 bottom-3 z-10 inline-flex items-center gap-2 bg-gold/95 text-black font-accent uppercase tracking-[0.22em] text-[10px] px-3.5 py-2 hover:bg-ivory transition-colors shadow-lg"
            aria-label="Play product video"
          >
            <Play size={11} fill="currentColor" /> Play Video
          </button>
        )}
        {/* PREV / NEXT (desktop) */}
        {effectiveImages.length > 1 && (
          <>
            <button
              onClick={prev}
              data-testid="gallery-prev"
              className="hidden md:flex absolute left-4 top-1/2 -translate-y-1/2 w-10 h-10 items-center justify-center bg-black/50 hover:bg-gold hover:text-black text-white transition-colors"
              aria-label="Previous image"
            >
              <ChevronLeft size={18} />
            </button>
            <button
              onClick={next}
              data-testid="gallery-next"
              className="hidden md:flex absolute right-4 top-1/2 -translate-y-1/2 w-10 h-10 items-center justify-center bg-black/50 hover:bg-gold hover:text-black text-white transition-colors"
              aria-label="Next image"
            >
              <ChevronRight size={18} />
            </button>
          </>
        )}
        {/* Mobile dots */}
        {effectiveImages.length > 1 && (
          <div className="md:hidden absolute bottom-4 left-0 right-0 flex justify-center gap-1.5">
            {effectiveImages.map((im, i) => (
              <span key={`dot-${im.key}`} className={`w-1.5 h-1.5 rounded-full transition-colors ${i === activeIdx ? "bg-gold" : "bg-white/40"}`} />
            ))}
          </div>
        )}
      </div>

      {/* THUMBNAILS */}
      {effectiveImages.length > 1 && (
        <div className="grid grid-cols-5 gap-2 mt-3" data-testid="gallery-thumbs">
          {effectiveImages.slice(0, 5).map((im, i) => (
            <button
              key={im.key}
              onClick={() => setActiveIdx(i)}
              data-testid={`gallery-thumb-${i}`}
              className={`relative aspect-square overflow-hidden border transition-colors ${i === activeIdx ? "border-gold" : "border-transparent hover:border-white/40"}`}
            >
              <img
                src={cdnTransform(im.url, "w_240,h_240,c_fill,q_auto,f_auto")}
                alt={im.alt || ""}
                loading="lazy"
                decoding="async"
                className="w-full h-full object-cover"
              />
            </button>
          ))}
        </div>
      )}

      {/* VIDEO MODAL */}
      {videoOpen && video && (
        <CinematicVideoModal video={video} onClose={() => setVideoOpen(false)} />
      )}
    </div>
  );
};

/**
 * Cinematic fullscreen video modal — supports Cloudinary upload + YouTube embed.
 * No related videos, dark cinematic chrome, ESC to close, click backdrop to close.
 */
export const CinematicVideoModal = ({ video, onClose }) => {
  // ESC to close + lock body scroll
  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape") onClose(); };
    document.addEventListener("keydown", onKey);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = prevOverflow;
    };
  }, [onClose]);

  return (
    <div
      data-testid="video-modal"
      className="fixed inset-0 z-[100] bg-black/95 backdrop-blur-sm flex items-center justify-center p-4 md:p-12"
      onClick={onClose}
    >
      <button
        data-testid="video-modal-close"
        onClick={onClose}
        aria-label="Close video"
        className="absolute top-6 right-6 w-12 h-12 flex items-center justify-center bg-black/60 hover:bg-gold hover:text-black text-white border border-white/20 transition-colors"
      >
        <X size={20} />
      </button>
      <div
        className="relative w-full max-w-6xl aspect-video bg-black shadow-[0_0_80px_rgba(212,175,55,0.15)]"
        onClick={(e) => e.stopPropagation()}
      >
        {video.type === "youtube" ? (
          <iframe
            data-testid="video-modal-yt"
            // Always reconstruct the nocookie embed URL from the youtube_id.
            // We never trust `video.url` (which is the watch URL) because YouTube
            // sends X-Frame-Options=sameorigin on watch pages, blocking the iframe.
            src={
              video.youtube_id
                ? `https://www.youtube-nocookie.com/embed/${video.youtube_id}?rel=0&modestbranding=1&playsinline=1&autoplay=1`
                : (video.embed_url || video.url)
            }
            title="Product video"
            className="w-full h-full"
            frameBorder="0"
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
            allowFullScreen
          />
        ) : (
          // Cloudinary upload — auto deliver MP4, controlled playback
          <video
            data-testid="video-modal-mp4"
            src={video.url}
            controls
            autoPlay
            playsInline
            className="w-full h-full object-contain bg-black"
            preload="metadata"
          />
        )}
      </div>
    </div>
  );
};

export default MediaGallery;
